// Lead feedback to the ad platforms: a signal is one business moment of a lead (event) reported
// to one channel. The table lead_feedback is the state (what still has to be sent, what was
// sent); the activity log holds every attempt. See docs/ad-conversion-signals.md.

// Value + merged type (see MaintenancePeriodUnit): the generated api client re-exports names as
// runtime values.
export const LeadStage = {
    leadCreated: 'lead_created',
    disqualifiedLead: 'disqualified_lead',
    schedule: 'schedule',
    noShow: 'no_show',
    quote: 'quote',
    purchase: 'purchase',
    lostDeal: 'lost_deal',
} as const;
export type LeadStage = (typeof LeadStage)[keyof typeof LeadStage];

export const LeadFeedbackChannel = {
    // One signed call to a partner endpoint that fans out to the platforms.
    relay: 'relay',
    metaCapi: 'meta_capi',
    googleDataManager: 'google_data_manager',
} as const;
export type LeadFeedbackChannel = (typeof LeadFeedbackChannel)[keyof typeof LeadFeedbackChannel];

export const LeadFeedbackStatus = {
    pending: 'pending',
    sent: 'sent',
    failed: 'failed',
    skipped: 'skipped',
    expired: 'expired',
} as const;
export type LeadFeedbackStatus = (typeof LeadFeedbackStatus)[keyof typeof LeadFeedbackStatus];

export const LeadFeedbackReason = {
    noMatchKeys: 'no_match_keys',
    channelNotAvailable: 'channel_not_available',
    channelNotConfigured: 'channel_not_configured',
    timeout: 'timeout',
    httpError: 'http_error',
    platformRejected: 'platform_rejected',
    outsidePlatformWindow: 'outside_platform_window',
} as const;
export type LeadFeedbackReason = (typeof LeadFeedbackReason)[keyof typeof LeadFeedbackReason];

export interface ILeadFeedbackEntity {
    lead_feedback_id: number;
    stage: LeadStage;
    channel_code: LeadFeedbackChannel;
    // Names the business moment (`schedule:planning_item:1234`); unique together with the channel.
    source_key: string;
    // What the platform receives: `{tenant prefix}:{source_key}`.
    event_id: string;
    planning_item_id: number;
    quote_id: number | null;
    occurred_at: Date;
    value: number | null;
    currency: string | null;
    status: LeadFeedbackStatus;
    status_reason: LeadFeedbackReason | null;
    attempt_count: number;
    next_attempt_at: Date | null;
    last_attempt_at: Date | null;
    sent_at: Date | null;
    creation_date: Date;
}

export interface ILeadFeedbackStageSettings {
    enabled: boolean;
}

// How a stage is described to a partner that maps our statuses itself: the entity it belongs
// to, the status name as the office knows it, and the reason where there is one.
export type LeadStageEntity = 'lead' | 'appointment' | 'quote';
export interface ILeadStageDescription {
    entity: LeadStageEntity;
    status: string;
    status_reason: string | null;
}
export const LEAD_STAGE_DESCRIPTION: { [code in LeadStage]: ILeadStageDescription } = {
    lead_created: { entity: 'lead', status: 'Nieuw', status_reason: null },
    disqualified_lead: { entity: 'lead', status: 'Protest', status_reason: null },
    schedule: { entity: 'appointment', status: 'Ingepland', status_reason: null },
    no_show: { entity: 'appointment', status: 'Niet thuis', status_reason: 'notHome' },
    quote: { entity: 'quote', status: 'Offerte opgemaakt', status_reason: null },
    purchase: { entity: 'quote', status: 'Goedgekeurd', status_reason: null },
    lost_deal: { entity: 'quote', status: 'Geannuleerd', status_reason: null },
};

// The event name Meta receives per stage: the standard names where Meta has one (Schedule,
// Purchase), the custom names agreed with the web partner for the rest. Fixed on purpose: a
// typo would silently file a stage under a new custom event. The relay receives the stage code
// and maps it itself; Google works with a conversion action per stage (its own setting).
export const META_EVENT_NAME: { [code in LeadStage]: string } = {
    lead_created: 'Lead',
    disqualified_lead: 'DisqualifiedLead',
    schedule: 'Schedule',
    no_show: 'NoShow',
    quote: 'Quote',
    purchase: 'Purchase',
    lost_deal: 'LostDeal',
};

// Where Meta is told the conversion happened. Meta reserves `system_generated` (with the CRM
// custom_data) for leads from Meta lead ads; website leads normally report as `website`. The
// web partner decides what fits the campaign objective, so it is a setting with a fixed vocabulary.
// Value + merged type (see LeadStage): the generated api client re-exports names as runtime values.
export const MetaActionSource = {
    website: 'website',
    systemGenerated: 'system_generated',
    other: 'other',
} as const;
export type MetaActionSource = (typeof MetaActionSource)[keyof typeof MetaActionSource];
export const META_ACTION_SOURCES = Object.values(MetaActionSource) as MetaActionSource[];

export interface ILeadFeedbackMetaSettings {
    // The dataset (pixel) id that receives the events; the access token is a secret.
    datasetId: string | null;
    apiVersion: string;
    actionSource: MetaActionSource;
    // Routes events to "Test events" in Events Manager while set; empty in production.
    testEventCode: string | null;
}

// Google's consent signal on an upload (Data Manager API ConsentStatus). One fixed value per
// tenant, chosen by the admin: EEA uploads without it are not used. Value + merged type.
export const GoogleConsentStatus = {
    granted: 'CONSENT_GRANTED',
    denied: 'CONSENT_DENIED',
} as const;
export type GoogleConsentStatus = (typeof GoogleConsentStatus)[keyof typeof GoogleConsentStatus];
export const GOOGLE_CONSENT_STATUSES = Object.values(GoogleConsentStatus) as GoogleConsentStatus[];

// Spelled out per stage (not a mapped type): tsoa drops the null of a mapped string value.
export interface ILeadFeedbackGoogleConversionActionIds {
    lead_created: string | null;
    disqualified_lead: string | null;
    schedule: string | null;
    no_show: string | null;
    quote: string | null;
    purchase: string | null;
    lost_deal: string | null;
}

export interface ILeadFeedbackGoogleSettings {
    // Google Ads customer id (digits) that owns the conversion actions.
    operatingAccountId: string | null;
    // Manager account the service account was added to, when access goes through one.
    loginAccountId: string | null;
    // One Google Ads conversion action ("Import: from clicks, other data sources") per stage;
    // a stage without one is not reported to Google.
    conversionActionIds: ILeadFeedbackGoogleConversionActionIds;
    // null = not chosen yet: the channel stays "not configured" until the admin decides.
    consent: {
        adUserData: GoogleConsentStatus | null;
        adPersonalization: GoogleConsentStatus | null;
    };
    // Dry run: Google validates the request and records nothing. For the first days.
    validateOnly: boolean;
}

export interface ILeadFeedbackSettings {
    enabled: boolean;
    // `relay`: one call to the partner endpoint; `direct`: our own Meta / Google adapters.
    route: 'relay' | 'direct';
    relay: {
        url: string | null;
        // Header that carries the secret verbatim (e.g. "x-authorization") for a partner that
        // checks a key instead of the signature; null = signature only.
        authHeader: string | null;
    };
    meta: ILeadFeedbackMetaSettings;
    google: ILeadFeedbackGoogleSettings;
    // ISO 3166-1 alpha-2 of the tenant: completes national phone numbers and is the country the
    // platforms match on when the lead has none.
    country: string;
    events: { [code in LeadStage]: ILeadFeedbackStageSettings };
    sendValue: boolean;
    // Which quote amount goes with a sale: the subtotal ex VAT, or the final price to pay.
    valueIncludesVat: boolean;
    currency: string;
}

export const LEAD_FEEDBACK_SETTINGS_FALLBACK: ILeadFeedbackSettings = {
    enabled: false,
    route: 'relay',
    relay: { url: null, authHeader: null },
    meta: { datasetId: null, apiVersion: 'v25.0', actionSource: 'website', testEventCode: null },
    google: {
        operatingAccountId: null,
        loginAccountId: null,
        conversionActionIds: {
            lead_created: null,
            disqualified_lead: null,
            schedule: null,
            no_show: null,
            quote: null,
            purchase: null,
            lost_deal: null,
        },
        consent: { adUserData: null, adPersonalization: null },
        validateOnly: true,
    },
    country: 'BE',
    events: {
        // Only Meta's lead-ads flow needs the first stage; website leads have the pixel's Lead.
        lead_created: { enabled: false },
        disqualified_lead: { enabled: true },
        schedule: { enabled: true },
        no_show: { enabled: true },
        quote: { enabled: true },
        purchase: { enabled: true },
        lost_deal: { enabled: true },
    },
    sendValue: true,
    valueIncludesVat: false,
    currency: 'EUR',
};

// Secrets live in integration_secret, encrypted, and are never returned by the API.
export const IntegrationSecretKey = {
    relaySigningSecret: 'lead_feedback.relay.signing_secret',
    metaAccessToken: 'lead_feedback.meta.access_token',
    // The service account key file (JSON) as Google hands it out.
    googleServiceAccount: 'lead_feedback.google.service_account_json',
} as const;
export type IntegrationSecretKey = (typeof IntegrationSecretKey)[keyof typeof IntegrationSecretKey];

export interface IIntegrationSecretStatus {
    secret_key: IntegrationSecretKey;
    set: boolean;
    // Last characters of the value, for an admin to recognise which secret is stored.
    hint: string | null;
    modified_date: Date | null;
}

export interface IIntegrationSecretUpdate {
    secret_key: IntegrationSecretKey;
    value: string;
}

// Per lead type: whether its leads report feedback, and to which channels.
export interface ILeadTypeFeedbackSettings {
    enabled: boolean;
    channel_codes: LeadFeedbackChannel[];
}
